import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { FiCalendar } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa6';
import './Contact.scss';

export default function Contact({ datosNuevos }) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const recaptchaRef = useRef(null);

  // 🎯 VARIABLES DINÁMICAS (Se adaptan con la prop datosNuevos o usan Empresas por defecto)
  const tituloContacto = datosNuevos?.title || "Lleva la contabilidad de tu empresa al siguiente nivel";
  const descripcionContacto = datosNuevos?.description || "Agenda una asesoría gratuita y descubre cómo podemos ayudarte a optimizar tus procesos contables, fiscales y financieros.";
  const placeholderDinamico = datosNuevos?.placeholderActividad || "Nombre de tu empresa";
  const esFreelancer = !!datosNuevos; // Bandera booleana para saber en qué vista andamos

  const onSubmit = async (data) => {
    const recaptchaValue = recaptchaRef.current ? recaptchaRef.current.getValue() : null;
    
    if (!recaptchaValue) {
      alert("Por favor, verifica que no eres un robot");
      return;
    }

    // Identificador para saber qué poner en el campo variable
    const campoVariableNombre = esFreelancer ? "Actividad profesional" : "Nombre de la empresa";
    const valorCampoVariable = esFreelancer ? data.actividad : data.empresa;

    const textoHtml = `
      <h3>Nuevo prospecto desde Landing ${esFreelancer ? 'Freelancers y Honorarios' : 'Empresas y PYMES'}</h3>
      <p><strong>Nombre completo:</strong> ${data.nombre}</p>
      <p><strong>${campoVariableNombre}:</strong> ${valorCampoVariable}</p>
      <p><strong>WhatsApp:</strong> ${data.whatsapp}</p>
      <p><strong>Correo electrónico:</strong> ${data.correo}</p>
      <p><strong>Servicio de interés:</strong> ${data.servicio}</p>
    `;

    const payload = {
      to: "contacto@despachozwolf.com",
      subject: esFreelancer 
        ? `Nueva Asesoría Freelancer - ${data.nombre} (${valorCampoVariable})`
        : `Nueva Asesoría Solicitada - ${data.empresa}`,
      text: textoHtml
    };

    try {
      const response = await fetch('https://despachozwolf.com/api/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const resultado = await response.json();

      if (resultado.status === 'Correo enviado con éxito') {
        alert("¡Asesoría solicitada con éxito!");
        reset();
        if (recaptchaRef.current) recaptchaRef.current.reset();
      } else {
        alert("Hubo un detalle en el servidor al enviar: " + resultado.status);
      }
    } catch (error) {
      console.error("Error enviando el formulario:", error);
      alert("No se pudo conectar con el servidor de correos.");
    }
  };

  return (
    <section id="section1" className="contact-section-wrapper">
      
      {/* SECCIÓN PANORÁMICA CON EL FONDO Y DEGRADADO EXACTO DEL BOCETO */}
      <div 
        className="contact-main-section" 
        id="contacto" 
        style={{ 
          backgroundImage: `linear-gradient(to right, rgba(17, 24, 39, 0.4) 0%, rgba(15, 23, 42, 0.88) 45%, rgba(15, 23, 42, 0.98) 100%), url("/img/contabilidad.webp")`
        }}
      >
        <div className="contact-content-container">
          
          {/* COLUMNA IZQUIERDA: TEXTOS DINÁMICOS Y REJILLA DE BENEFICIOS */}
          <div className="contact-left-info">
            <span className="contact-tag">ASESORÍA PERSONALIZADA</span>
            <h2 className="contact-heading">{tituloContacto}</h2>
            <p className="contact-subheading">{descripcionContacto}</p>

            <div className="contact-benefits-row">
              <div className="benefit-item">
                <div className="benefit-icon-wrapper">
                  <FiCalendar />
                </div>
                <div className="benefit-text">
                  <h4>Asesoría sin costo</h4>
                  <p>Conoce cómo podemos ayudarte.</p>
                </div>
              </div>

              {/* Línea horizontal divisoria */}
              <div className="benefit-divider-line"></div>

              <div className="benefit-item">
                <div className="benefit-icon-wrapper">
                  <FaWhatsapp />
                </div>
                <div className="benefit-text">
                  <h4>Respuesta rápida</h4>
                  <p>Te atendemos por WhatsApp en minutos.</p>
                </div>
              </div>
            </div>
          </div>

          {/* COLUMNA DERECHA: LA TARJETA BLANCA IMPECABLE */}
          <div className="contact-card-right">
            <div className="form-card-header">
              <h3>Agenda tu asesoría</h3>
              <p>Déjanos tus datos y nos pondremos en contacto contigo.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="z-form">
              <div className="z-form-group">
                <input 
                  type="text" 
                  placeholder="Nombre completo" 
                  className={errors.nombre ? 'z-input error' : 'z-input'} 
                  {...register("nombre", { required: true })} 
                />
              </div>

              {/* 🎯 INPUT CAMBIANTE: Se registra como 'actividad' o 'empresa' según corresponda */}
              <div className="z-form-group">
                <input 
                  type="text" 
                  placeholder={placeholderDinamico} 
                  className={(errors.empresa || errors.actividad) ? 'z-input error' : 'z-input'} 
                  {...register(esFreelancer ? "actividad" : "empresa", { required: true })} 
                />
              </div>

              <div className="z-form-row">
                <div className="z-form-group half">
                  <div className="input-with-icon">
                    <FaWhatsapp className="inner-icon" />
                    <input 
                      type="tel" 
                      placeholder="WhatsApp" 
                      className={errors.whatsapp ? 'z-input error' : 'z-input'} 
                      {...register("whatsapp", { required: true })} 
                    />
                  </div>
                </div>

                <div className="z-form-group half">
                  <input 
                    type="email" 
                    placeholder="Correo electrónico" 
                    className={errors.correo ? 'z-input error' : 'z-input'} 
                    {...register("correo", { required: true })} 
                  />
                </div>
              </div>

              {/*  SELECT DINÁMICO: Cambia las opciones de servicios según el público meta */}
              <div className="z-form-group select-group">
                <label className="z-select-label">Servicio de interés</label>
                <select 
                  className={errors.servicio ? 'z-select error' : 'z-select'} 
                  defaultValue="" 
                  {...register("servicio", { required: true })}
                >
                  <option value="" disabled>Selecciona una opción</option>
                  {esFreelancer ? (
                    <>
                      <option value="Declaraciones">Declaraciones Mensuales / Anuales</option>
                      <option value="RESICO">Asesoría contable</option>
                      <option value="Facturacion">Facturación Electrónica CFDI</option>
                      <option value="Regularizacion">Regularización Fiscal ante el SAT</option>
                    </>
                  ) : (
                    <>
                      <option value="Contabilidad">Contabilidad Profesional</option>
                      <option value="Auditoria">Auditoría Fiscal/Financiera</option>
                      <option value="Finanzas">Optimización Financiera</option>
                      <option value="IMSS">Asesoría Pensiones IMSS</option>
                    </>
                  )}
                </select>
              </div>

              {/* RECAPTCHA Y BOTÓN DE ENVÍO */}
              <div className="z-action-row">
                <div className="z-captcha-container">
                  <ReCAPTCHA 
                    ref={recaptchaRef} 
                    sitekey="6LfFFKsUAAAAALSTw3iqSJ0liB7bxn0j8WFxyHdU"
                    size="compact" 
                    hl="es"
                  />
                </div> 
                
                <button type="submit" className="z-submit-btn">
                  Solicitar información <span className="btn-arrow">→</span>
                </button>
              </div>

              <div className="whatsapp-footer-link">
                <a href="https://wa.me/523325446331" target="_blank" rel="noreferrer">
                  <FaWhatsapp /> Hablar por WhatsApp
                </a>
              </div>
            </form>
          </div>

        </div>
      </div>
      
    </section>
  );
}